import { useUser } from '~/hooks/useUser';
import { useSpeculatorSelectionStore } from '~/store/useBuyerMatchSelectionStore';
import { SpeculatorCardVariantProps } from './Variants';
import { Link } from '@chakra-ui/next-js';
import { useSearchParams } from 'next/navigation';
import {
  Avatar,
  Badge,
  Box,
  Checkbox,
  Flex,
  HStack,
  Stack,
  Text,
} from '@chakra-ui/react';
import { ListSkeleton } from '~/components/homes/list/components/ListSkeleton';
import {
  formatAreaDisplay,
  formatBuyerData,
  formatBuyerSearchingFor,
  getFullName,
} from '@habity/services';
import { isBrokerOrAdmin, isAdmin } from '~/lib/roles';
import { buildSimpleUserAddress } from '~/lib/users/address';
import { isPublicSpeculator, type NestedSpeculator } from '@habity/types';
import { ContactDetails } from '~/components/ui/cards/components/ContactDetails';
import { HabityTooltip } from '~/components/ui/tooltips';
import { useBuyerDetails as useSpeculatorDetails } from '~/hooks/useBuyerDetails';
import { ScoreBreakdown } from './components/ScoreBreakdown';

const getScoreColor = (score: number): string => {
  const pct = Math.max(0, Math.min(100, score));
  if (pct >= 95) return 'green.300';
  if (pct >= 85) return 'green.200';
  if (pct >= 75) return 'green.100';
  if (pct >= 65) return 'teal.200';
  if (pct >= 55) return 'teal.100';
  if (pct >= 45) return 'yellow.200';
  if (pct >= 35) return 'yellow.300';
  if (pct >= 25) return 'orange.200';
  if (pct >= 15) return 'orange.300';
  return 'red.200';
};

const getScoreLabel = (score: number): string => {
  const rounded = Math.round(score);
  if (rounded >= 100) return 'Perfekt match';
  return `${rounded}% match`;
};

// FIXME: THIS IS EXTREMELY UGLY AND MUST BE REDESIGNED ASAP
export const GridSpeculatorCard = ({
  speculator,
  href,
  isLoading = false,
}: SpeculatorCardVariantProps) => {
  const { areas, avatar_url } = speculator || {};
  const { user } = useUser();
  const { speculatorsSelection, toggleSpeculatorSelection } =
    useSpeculatorSelectionStore();
  const { showSpeculatorDetails } = useSpeculatorDetails();

  // Check if address is present in search params label
  const searchParams = useSearchParams();
  const label = searchParams?.get('label');
  const hasAddress = label?.includes('address:') ?? false;
  const NON_FILTER_PARAMS = new Set([
    'showBuyerDetails',
    'tab',
    'label',
    'page',
  ]);
  const hasFilters = Array.from(searchParams?.keys() ?? []).some(
    (key) => !NON_FILTER_PARAMS.has(key)
  );

  const searchedAreaIds =
    searchParams?.get('areaIds')?.split(',').map(Number) ?? [];
  const displayArea = formatAreaDisplay(areas, searchedAreaIds);

  // Get user's address for display (only available for full buyer data, not public)
  const userAddress = !isPublicSpeculator(speculator)
    ? buildSimpleUserAddress(speculator)
    : null;

  const showMatchScore = isBrokerOrAdmin(user) && !isLoading && hasFilters;
  const isSelectionEnabled = isBrokerOrAdmin(user) && hasAddress;

  // Create the card content
  const cardContent = (
    <Flex
      direction="column"
      alignItems="left"
      rounded="4px"
      padding="40px 20px 20px 20px" // FIXME:
      position="relative"
      backgroundColor="blackAlpha.100"
      cursor={isLoading ? 'not-allowed' : 'pointer'}
    >
      {/* Top-right */}
      <HStack position="absolute" top={3} right={3} zIndex="2">
        {showMatchScore && (
          <HStack spacing={1}>
            <Badge
              size="sm"
              px={2}
              backgroundColor={getScoreColor(speculator.match_score)}
            >
              {getScoreLabel(speculator.match_score)}
            </Badge>
            <ScoreBreakdown speculator={speculator} />
          </HStack>
        )}

        {/* Selection checkbox */}
        {isSelectionEnabled && !isLoading && (
          <Box
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleSpeculatorSelection(speculator as NestedSpeculator);
            }}
          >
            <HabityTooltip label="Välj spekulant för att skicka meddelande">
              <HStack>
                <Checkbox
                  isChecked={speculatorsSelection
                    .map((b) => b.id)
                    .includes(speculator.id)}
                  colorScheme="blackAlpha"
                  borderColor="blackAlpha.600"
                />
              </HStack>
            </HabityTooltip>
          </Box>
        )}
      </HStack>

      {/* Card contents */}
      <Stack gap="4">
        {/* Image, Details */}
        <HStack gap="4" h="80px" alignItems="start">
          {/* Avatar */}
          <ListSkeleton borderRadius="full" isLoaded={!isLoading}>
            <Avatar
              color="blackAlpha.600"
              backgroundColor="white"
              size="lg"
              src={avatar_url || undefined}
              name={getFullName(speculator)}
            />
          </ListSkeleton>

          {/* Description, Features */}
          <Stack spacing="1" flex="1">
            <ListSkeleton isLoaded={!isLoading}>
              <Text fontSize="sm" color="blackAlpha.800">
                {formatBuyerSearchingFor(speculator.search, displayArea)}
              </Text>
            </ListSkeleton>

            <ListSkeleton isLoaded={!isLoading}>
              <Text fontSize="xs" fontWeight="medium">
                {formatBuyerData(speculator.search)}
              </Text>
            </ListSkeleton>
          </Stack>
        </HStack>

        {/* Contact details — always reserve space when details mode is on for equal card heights */}
        {showSpeculatorDetails && isBrokerOrAdmin(user) && (
          <Box p={0} minH="48px">
            {(speculator?.phone || speculator?.email || userAddress) && (
              <ListSkeleton isLoaded={!isLoading}>
                <ContactDetails
                  size="xs"
                  location={userAddress || displayArea}
                  phone={speculator?.phone}
                  email={speculator?.email}
                />
              </ListSkeleton>
            )}
          </Box>
        )}
      </Stack>
    </Flex>
  );

  // If loading, don't wrap with Link
  if (isLoading) return cardContent;

  // Otherwise, wrap with Link for navigation
  return (
    <Link
      href={href}
      _hover={{
        textDecoration: 'none',
      }}
    >
      {cardContent}
    </Link>
  );
};
